import Switch from '@mui/material/Switch';
import { Link, useNavigate } from 'react-router-dom';
import * as React from 'react';
import Vod from '../../services/interfaces/vods-interface';
import EventService from '../../services/event-service';
import ManageLiveEventService from '../Go Live/ManageLiveEventService';
import { isLoggedIn } from '../../services/isLoggedInService';
import { updateVisibility } from '../../services/visibilityPublishService';
import { RingLoader } from 'react-spinners';
import { IconButton, Menu, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/ModeEdit';
import BarChartIcon from '@mui/icons-material/StackedBarChart';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const ITEM_HEIGHT = 48;

interface VideoProps {
  loggedInProvider: { id: string; userType: string };
}

const Video: React.FC<VideoProps> = ({ loggedInProvider }) => {
  const [vods, setVods] = React.useState<Vod[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterName, setFilterName] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'views' | 'comments' | 'name'>('views');

  const navigate = useNavigate();
  const manageLiveEventService = new ManageLiveEventService();
  const open = Boolean(anchorEl);
  const category = 'vods';

  // 🔐 Auth
  React.useEffect(() => {
    if (!isLoggedIn()) navigate('/auth/signin');
  }, [navigate]);

  // 📦 Fetch + normalize VODs
  React.useEffect(() => {
    const fetchVods = async () => {
      try {
        setLoading(true);

        const fetchedVods = await EventService.getNewSchemaEvents();

        const normalized = fetchedVods
          .filter((vod) => vod.category === 'vod')
          .map((vod) => ({
            ...vod,
            views: typeof vod.views === 'number' ? vod.views : 0,
            comments: typeof vod.comments === 'number' ? vod.comments : 0,
            video_list: Array.isArray(vod.video_list) ? vod.video_list : [],
            status: {
              type: vod.status?.type || 'normal',
              lifetime: vod.status?.lifetime ?? 0,
              visibility: Boolean(vod.status?.visibility),
              publish:
                vod.status?.publish === 'true'
                  ? true
                  : vod.status?.publish === 'false'
                  ? false
                  : Boolean(vod.status?.publish),
              promoted: Boolean(vod.status?.promoted),
              live: Boolean(vod.status?.live),
            },
          }));

        setVods(normalized);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching VODs:', error);
        setLoading(false);
      }
    };

    fetchVods();
  }, []);

  // 🔁 Toggle visibility / publish
  const handleToggleChange = async (
    vodId: string,
    field: 'visibility' | 'publish',
    newValue: boolean
  ) => {
    try {
      const current = vods.find((v) => v._id === vodId);
      if (!current) return;

      await updateVisibility(vodId, category, {
        visibility: field === 'visibility' ? newValue : current.status.visibility,
        publish: field === 'publish' ? newValue : current.status.publish,
      });

      setVods((prev) =>
        prev.map((vod) =>
          vod._id === vodId
            ? { ...vod, status: { ...vod.status, [field]: newValue } }
            : vod
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // 🔍 Filter + sort
  const filteredVods = vods
    .filter(
      (vod) =>
        loggedInProvider.userType === 'admin' ||
        vod.video_provider === loggedInProvider.id
    )
    .filter((vod) =>
      vod.title?.toLowerCase().includes(filterName.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'views') return (b.views || 0) - (a.views || 0);
      if (sortBy === 'comments') return (b.comments || 0) - (a.comments || 0);
      return (a.title || '').localeCompare(b.title || '');
    });

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-transparent dark:bg-transparent w-[1150px]">
      {/* Controls */}
      <div className="m-4 mt-2 flex justify-between px-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Sort by:</span>
          <select
            className="rounded border px-2 py-1 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <option value="views">Views</option>
            <option value="comments">Comments</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Filter by name:</span>
          <input
            type="text"
            className="rounded-full border px-3 py-1"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
      </div>

      {/* ✅ COLUMN HEADER ROW (MATCHES LiveEvent) */}
      <div className="grid grid-cols-12 border-t border-stroke px-4 py-2 font-medium">
        <div className="col-span-5">Name</div>
        <div className="col-span-1 hidden sm:flex justify-center">Clicks</div>
        <div className="col-span-1 flex justify-center">Views</div>
        <div className="col-span-1 flex justify-center">Comments</div>
        <div className="col-span-1 flex justify-center">Visibility</div>
        <div className="col-span-1 flex justify-center">Publish</div>
        <div className="col-span-1 flex justify-center">Manage</div>
      </div>

      {/* Rows */}
      <div className="m-4 mt-2 h-screen overflow-y-scroll">
        {loading ? (
          <RingLoader color="#36D7B7" size={100} />
        ) : (
          filteredVods.map((vod) => (
            <div
              key={vod._id}
              className="grid grid-cols-12 border-t border-stroke px-4 py-2"
            >
              <div className="col-span-5 flex items-center gap-4">
                <img
                  src={vod.thumb_nail}
                  alt={vod.title}
                  className="h-20 w-20 rounded-md"
                />
                <p>{vod.title}</p>
              </div>

              {/* Clicks */}
              <div className="col-span-1 hidden sm:flex justify-center">
                {vod.video_list?.length || 0}
              </div>

              {/* Views */}
              <div className="col-span-1 flex justify-center">
                {vod.views || 0}
              </div>

              {/* Comments */}
              <div className="col-span-1 flex justify-center">
                {vod.comments || 0}
              </div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={vod.status.visibility}
                  onChange={() =>
                    handleToggleChange(vod._id, 'visibility', !vod.status.visibility)
                  }
                />
              </div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={vod.status.publish}
                  onChange={() =>
                    handleToggleChange(vod._id, 'publish', !vod.status.publish)
                  }
                />
              </div>

              <div className="col-span-1 flex justify-center">
                <Link to={`/ManageVideoEdit/${vod._id}?category=vods`}>
                  <EditIcon fontSize="small" />
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Video;
